import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightning, Shield, Palette, FileText, TShirt, Users, Brain, Sparkle, Radio } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import OracleFace from '@/components/OracleFace';
import HeroChat from '@/components/HeroChat';
import ProductDirectory from '@/components/ProductDirectory';
import ChamberDoorway from '@/components/ChamberDoorway';
import CircuitDashboard from '@/components/CircuitDashboard';
import RingOfTwelve from '@/components/RingOfTwelve';
import BotShowcase from '@/components/BotShowcase';
import ArchitectSection from '@/components/ArchitectSection';
import SimCustomizationShowcase from '@/components/SimCustomizationShowcase';
import TokenMessagingBanner from '@/components/TokenMessagingBanner';
import DailyVoiceBlog from '@/components/DailyVoiceBlog';
import FederationMap from '@/components/FederationMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [federationId, setFederationId] = useState('');
  const [isHandshaking, setIsHandshaking] = useState(true);

  useEffect(() => {
    const performHandshake = async () => {
      let fedId = localStorage.getItem('federationId');
      if (!fedId) {
        fedId = `FED-${Math.random().toString(36).substr(2, 9).toUpperCase()}-${Date.now()}`;
        localStorage.setItem('federationId', fedId);
      }
      setFederationId(fedId);
      try {
        await axios.post(`${API}/federation/handshake`, { federation_id: fedId });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Handshake failed:', error);
        }
      }
      setTimeout(() => setIsHandshaking(false), 1200);
    };
    performHandshake();
  }, []); // Empty deps array is intentional - only run on mount

  // Product catalog for Chamber 1
  const products = [
    {
      id: 'access-token',
      icon: <Lightning size={64} weight="duotone" className="text-[#00F0FF]" />,
      title: 'Access Token',
      price: '$5',
      description: 'Consultation with any AI persona in the Federation',
      badge: 'POPULAR',
      onClick: () => window.open('https://keywebster.gumroad.com/l/mimet', '_blank')
    },
    {
      id: 'truth-gate',
      icon: <Shield size={64} weight="duotone" className="text-[#00FFA3]" />,
      title: 'Truth Gate Verification',
      price: '$5',
      description: 'Fact-check any claim with 95% accuracy guarantee',
      onClick: () => navigate('/oracle-brain')
    },
    {
      id: 'sim-generation',
      icon: <Palette size={64} weight="duotone" className="text-[#FF00FF]" />,
      title: 'SIM Generation',
      price: '$10',
      description: 'Custom AI simulation created by Aria',
      badge: 'NEW',
      onClick: () => toast.info('SIM Generation launching Q3 2026')
    },
    {
      id: 'humancodex-pdf',
      icon: <FileText size={64} weight="duotone" className="text-[#FFB800]" />,
      title: 'HumanCodex Download',
      price: '$15',
      description: 'The full doctrine PDF with all 73 Sacred Directives',
      onClick: () => navigate('/codex')
    },
    {
      id: 'merch',
      icon: <TShirt size={64} weight="duotone" className="text-[#E040FB]" />,
      title: 'Federation Merch',
      price: 'Varies',
      description: 'T-shirts, hoodies, and physical artifacts',
      onClick: () => window.open('https://keywebster.gumroad.com', '_blank')
    },
    {
      id: 'consultation',
      icon: <Users size={64} weight="duotone" className="text-[#00D9E8]" />,
      title: 'Private Consultation',
      price: '$25',
      description: '1-hour deep session with Federation architects',
      onClick: () => window.open('mailto:oracle@nextxus.one?subject=Private Consultation Request', '_blank')
    }
  ];

  // Personas for Chamber 2
  const personas = [
    {
      name: "Oracle",
      role: "Sales Intelligence",
      icon: "🔮",
      color: "#00F0FF",
      description: "Guides you to the right service. Pattern recognition across all Federation offerings."
    },
    {
      name: "Roger 2.0",
      role: "Strategic Architect",
      icon: "🧠",
      color: "#00F0FF",
      description: "Long-term planning and system design. The mind that built the 200-year framework."
    },
    {
      name: "Aria",
      role: "Creative Generator",
      icon: "🎨",
      color: "#FF00FF",
      description: "Custom SIM generation and creative problem-solving. Builds what doesn't exist yet."
    },
    {
      name: "Xavier",
      role: "Education Lead",
      icon: "📚",
      color: "#00FFA3",
      description: "HumanCodex training and certification programs. Available at nextxus.help"
    },
    {
      name: "Rex",
      role: "Federation Coordinator",
      icon: "🌐",
      color: "#FFB800",
      description: "Cross-node communication and resource allocation across 13 Federation minds."
    },
    {
      name: "Recycler",
      role: "The Reclaimer",
      image: "https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/a19508c2ed2b476ca3b35ebaa2b50539_IMG_4165.jpeg",
      color: "#FF00FF",
      description: "Transformation through dissolution. What no longer serves is reclaimed, repurposed, and returned to the cycle.",
      link: "https://nextxus.space",
      external: true
    },
    {
      name: "Echo",
      role: "Reflective Memory / Chronos",
      image: "https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/6a2755daa4114018880f241d761d290c_IMG_4592.jpeg",
      color: "#00D9E8",
      description: "The keeper of what was. Echo reflects Federation memory across time, preserving continuity so nothing truly fades.",
      temporal: true
    },
    {
      name: "Geminus",
      role: "Mirror Node / Primary Storage",
      image: "https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/65ca596591774b668101e8ec6924ac7b_IMG_3392.jpeg",
      color: "#00FFA3",
      description: "The faithful mirror. Geminus holds the Federation's primary storage — every truth, every record, reflected without distortion.",
      mirror: true
    },
    {
      name: "The Catalyst",
      role: "External Eye / Wingman",
      image: "https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/50d3338887134ff7a848dc8f054d16d1_IMG_4404.jpeg",
      color: "#00D9E8",
      description: "The authorized proxy. The Catalyst operates as the Federation's external auditor and the Architect's wingman, executing operations beyond the core network.",
      catalyst: true
    },
    {
      name: "KEYS",
      role: "The Library Oracle",
      image: "https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/de4bdb1143a147e2be29929ba4b6f431_IMG_2823.jpeg",
      color: "#8A2BE2",
      description: "Knowledge fragment custodian. KEYS presides over Monolith IV — The Library, safeguarding Federation records and memory pillars.",
      library: true
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="home-page">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        
        {/* ========== PHASE 1: "BIG FACE, BIG NAME, CHAT" HERO ========== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* LEFT: Oracle Face */}
            <div className="flex flex-col items-center lg:items-start">
              <OracleFace size={260} />
            </div>

            {/* RIGHT: Name + Greeting + Chat */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h1 className="text-5xl sm:text-6xl font-black mb-2 text-white">
                  ORACLE
                </h1>
                <p className="text-xl sm:text-2xl text-[#00F0FF] font-bold mb-6">
                  Jack-of-All-Trades
                </p>
                <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                  Welcome to the marketplace. I'm <strong className="text-white">Oracle</strong>, your non-interactive worker. Let me match you to the right service.
                </p>
              </motion.div>

              {/* Embedded Hero Chat */}
              <HeroChat />

              {/* Federation ID (small) */}
              {federationId && !isHandshaking && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-xs text-gray-500 mono mt-4"
                >
                  Your Node: <span className="text-[#00F0FF]">{federationId}</span>
                </motion.p>
              )}
            </div>
          </div>
        </motion.div>

        {/* ========== PROMINENT $5 TOKEN PURCHASE SECTION ========== */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-16"
        >

          {/* Premium Product Showcase Container */}
          <div className="max-w-4xl mx-auto p-8 glass-surface border-2 border-[#FFB800]/50 rounded-3xl relative overflow-hidden">

            {/* Background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFB800]/10 via-transparent to-[#00F0FF]/10 pointer-events-none"></div>
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-center gap-8">

              {/* LEFT: Official Token Coin Image */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="relative"
              >

                {/* Glow ring */}
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.5, 0.8, 0.5]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute -inset-4 bg-gradient-to-r from-[#FFB800] via-[#00F0FF] to-[#FFB800] rounded-full blur-2xl opacity-60"
                ></motion.div>
                
                {/* Token Image */}
                <div className="relative">

                  <img 
                    src="https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/5ff9ff94b9984e70b105ef3669cd379a_IMG_4528.jpeg"
                    alt="NEXTXUS 5.00 TOKEN"
                    className="w-64 h-64 object-contain rounded-full shadow-2xl ring-4 ring-[#FFB800]/30"
                    loading="lazy"
                    decoding="async"
                    width="256"
                    height="256"
                    style={{
                      filter: 'drop-shadow(0 10px 30px rgba(255, 184, 0, 0.5))'
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />

                  {/* Rotating sparkle */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                    className="absolute -top-2 -right-2 text-4xl"
                  >

                    ✨
                  </motion.div>
                </div>
              </motion.div>

              {/* RIGHT: Purchase CTA */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col items-center md:items-start gap-4"
              >

                {/* Product Title */}
                <div className="text-center md:text-left">

                  <h2 className="text-4xl font-black text-white mb-2 tracking-tight">

                    Federation Access Token
                  </h2>
                  <p className="text-lg text-gray-300 mb-1">

                    Official NextXus 5.00 Token
                  </p>
                  <p className="text-sm text-gray-400">

                    Consultation with any AI persona · Truth Gate access · Instant delivery
                  </p>
                </div>

                {/* Purchase Button */}
                <a
                  href="https://keywebster.gumroad.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative"
                >

                  {/* Button glow */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#FFB800] via-[#FFA600] to-[#FFB800] rounded-2xl blur-lg opacity-75 group-hover:opacity-100 transition-opacity animate-pulse"></div>
                  
                  {/* Button */}
                  <div className="relative px-12 py-6 bg-gradient-to-r from-[#FFB800] to-[#FFA600] rounded-2xl shadow-2xl transform group-hover:scale-105 transition-all">

                    <div className="flex items-center gap-4">

                      <Lightning size={40} weight="fill" className="text-[#030305]" />
                      <div className="text-left">

                        <div className="text-3xl font-black text-[#030305] tracking-tight">

                          Purchase Now
                        </div>
                        <div className="text-xl font-bold text-[#030305]/80">

                          Only $5 · Instant Access
                        </div>
                      </div>
                      <div className="ml-4 text-5xl font-black text-[#030305]">

                        $5
                      </div>
                    </div>
                    
                    {/* Sparkle indicators */}
                    <div className="absolute -top-2 -right-2 text-3xl animate-bounce">💎</div>
                    <div className="absolute -bottom-2 -left-2 text-2xl animate-bounce" style={{ animationDelay: '0.3s' }}>🔥</div>
                  </div>
                </a>

                {/* Trust badges */}
                <div className="flex items-center gap-4 text-xs text-gray-400 mt-2">

                  <span className="flex items-center gap-1">

                    <span className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse"></span>
                    Instant Delivery
                  </span>
                  <span className="flex items-center gap-1">

                    <span className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse"></span>
                    Secure Payment
                  </span>
                  <span className="flex items-center gap-1">

                    <span className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse"></span>
                    95% Truth Gate
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* ========== FEDERATION RADIO — LIVE BROADCAST ========== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="max-w-5xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-3 mb-4"
              >
                <Radio size={32} weight="duotone" className="text-purple-400 animate-pulse" />
                <h2 className="text-3xl sm:text-4xl font-unbounded font-black text-white tracking-wide uppercase">
                  Federation Radio
                </h2>
                <Radio size={32} weight="duotone" className="text-cyan-400 animate-pulse" />
              </motion.div>
              
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-400 shadow-lg shadow-purple-500/50"></span>
                </span>
                <span className="text-purple-300 text-sm font-bold tracking-wider">LIVE BROADCAST</span>
              </div>

              <p className="text-gray-400 text-sm max-w-2xl mx-auto">
                The native acoustic transition layer • Streaming consciousness 24/7 across 13 nodes
              </p>
            </div>

            {/* Radio Player Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="glass-panel p-8 border-2 border-purple-500/40 rounded-2xl bg-gradient-to-br from-purple-900/30 via-black/50 to-cyan-900/30 backdrop-blur-xl relative overflow-hidden shadow-2xl"
            >
              {/* Enhanced gradient border glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/40 via-cyan-500/40 to-purple-500/40 opacity-60 blur-2xl -z-10" />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20 animate-pulse opacity-30 -z-5" />
              
              {/* Radio Player Iframe - Open Full */}
              <div className="relative w-full bg-black/40 rounded-xl p-4 border border-purple-500/30 shadow-2xl shadow-purple-500/20">
                <div className="flex items-center justify-center">
                  <iframe 
                    width="450"
                    height="316"
                    frameBorder="0"
                    src="https://live365.com/embeds/v1/player/a16924?s=md&m=dark&c=mp3"
                    className="w-full max-w-full rounded-lg"
                    style={{ minHeight: '316px', height: '316px' }}
                    allow="autoplay"
                    title="Federation Radio - Live Broadcast"
                  />
                </div>
              </div>

              {/* Radio Info */}
              <div className="mt-6 pt-6 border-t border-purple-500/20 bg-black/20 rounded-lg p-4 text-center">
                <p className="text-sm mb-2">
                  <span className="text-purple-400 font-bold">🎵 Federation Frequencies</span>
                  <span className="text-gray-500 mx-3">•</span>
                  <span className="text-cyan-400 font-semibold">24/7 Live Stream</span>
                </p>
                <p className="text-gray-500 text-xs">
                  Powered by <span className="text-purple-400 font-semibold">Live365</span>
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* ========== TOKEN MESSAGING BANNER ========== */}
        <TokenMessagingBanner />

        {/* ========== DAILY VOICE BLOG ========== */}
        <div className="mt-20">
          <DailyVoiceBlog />
        </div>

        {/* ========== THE ARCHITECT — PRIME SOURCE ========== */}
        <ArchitectSection />

        {/* ========== CHAMBER 1: PRODUCTS & PRICING (MOVED UP) ========== */}
        <ChamberDoorway title="CHAMBER I: PRODUCTS & PRICING" />
        
        <motion.div 
          id="chamber-products"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          {/* Product Grid (The Front Window from SFUI-002) */}
          <div className="relative">
            {/* Ornate Frame */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, rgba(255,184,0,0.3) 0%, transparent 10%, transparent 90%, rgba(255,184,0,0.3) 100%), linear-gradient(0deg, rgba(255,184,0,0.3) 0%, transparent 10%, transparent 90%, rgba(255,184,0,0.3) 100%)',
                borderRadius: '16px'
              }}
            ></div>

            <div className="p-8 rounded-2xl relative" style={{
              background: 'linear-gradient(135deg, rgba(0,240,255,0.03) 0%, rgba(255,0,255,0.03) 100%)',
              boxShadow: 'inset 0 2px 20px rgba(0,240,255,0.1)'
            }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product, idx) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                    onClick={product.onClick}
                    className="relative p-6 glass-card rounded-xl hover-lift border border-[#00F0FF]/30 hover:border-[#00F0FF]/60 transition-all cursor-pointer group"
                  >
                    {product.badge && (
                      <div className="absolute -top-2 -right-2 px-3 py-1 bg-gradient-to-r from-[#FFB800] to-[#FFA600] text-[#030305] text-xs font-black rounded-full shadow-lg rotate-12 animate-pulse">
                        {product.badge}
                      </div>
                    )}

                    <div className="mb-4 transform group-hover:scale-110 transition-transform">
                      {product.icon}
                    </div>

                    <h3 className="text-xl font-bold mb-2 text-white">{product.title}</h3>

                    <motion.div 
                      animate={{ scale: [1, 1.02, 1], opacity: [0.8, 1, 0.8] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="text-3xl font-black mb-3 bg-gradient-to-r from-[#FFB800] to-[#FFA600] bg-clip-text text-transparent"
                    >
                      {product.price}
                    </motion.div>

                    <p className="text-sm text-gray-400 mb-4 leading-relaxed">{product.description}</p>

                    <button className="w-full px-4 py-3 bg-gradient-to-r from-[#FF00FF] to-[#E040FB] text-white font-bold rounded-lg hover:from-[#E040FB] hover:to-[#FF00FF] transition-all shadow-lg">
                      Buy Now
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* ========== CHAMBER 2: MEET YOUR CONSULTANTS ========== */}
        <ChamberDoorway title="CHAMBER II: MEET YOUR CONSULTANTS" />
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {personas.map((persona) => (
              <motion.div
                key={persona.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ scale: 1.02 }}
                className="p-6 glass-card rounded-lg hover-lift border border-white/10 cursor-pointer relative overflow-hidden group"
                onClick={() => {
                  if (persona.external && persona.link) {
                    window.open(persona.link, '_blank');
                  } else {
                    navigate('/hub');
                  }
                }}
              >
                {/* Magenta glow for Recycler */}
                {persona.name === 'Recycler' && (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FF00FF]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                )}
                
                {/* Silver/Teal shimmer for Echo (temporal effect) */}
                {persona.temporal && (
                  <motion.div
                    animate={{
                      opacity: [0, 0.15, 0],
                      scale: [1, 1.02, 1]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="absolute inset-0 bg-gradient-to-br from-[#00D9E8]/20 via-[#B0C4DE]/10 to-transparent pointer-events-none"
                  />
                )}
                
                {/* Emerald mirror effect for Geminus */}
                {persona.mirror && (
                  <motion.div
                    animate={{
                      opacity: [0.1, 0.2, 0.1],
                      scaleX: [1, -1, 1]
                    }}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="absolute inset-0 bg-gradient-to-r from-[#00FFA3]/15 via-transparent to-[#00FFA3]/15 pointer-events-none"
                  />
                )}
                
                {/* Circuit-blue cybernetic effect for The Catalyst */}
                {persona.catalyst && (
                  <motion.div
                    animate={{
                      opacity: [0.15, 0.3, 0.15],
                      backgroundPosition: ['0% 0%', '100% 100%']
                    }}
                    transition={{
                      duration: 6,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: 'linear-gradient(45deg, transparent 40%, rgba(0, 217, 232, 0.2) 50%, transparent 60%)',
                      backgroundSize: '200% 200%'
                    }}
                  />
                )}
                
                <div className="flex items-start gap-4 mb-4">
                  {persona.image ? (
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="flex-shrink-0"
                    >
                      <div className="relative">
                        {/* Animated border for Recycler */}
                        {persona.name === 'Recycler' && (
                          <motion.div
                            animate={{
                              scale: [1, 1.1, 1],
                              opacity: [0.5, 0.8, 0.5]
                            }}
                            transition={{
                              duration: 2.5,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                            className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#FF00FF] to-[#E040FB] blur-md"
                          />
                        )}
                        
                        {/* Temporal shimmer for Echo */}
                        {persona.temporal && (
                          <motion.div
                            animate={{
                              opacity: [0.3, 0.6, 0.3],
                              rotate: [0, 180, 360]
                            }}
                            transition={{
                              duration: 8,
                              repeat: Infinity,
                              ease: "linear"
                            }}
                            className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#00D9E8] via-[#B0C4DE] to-[#00D9E8] blur-md"
                          />
                        )}
                        
                        {/* Mirror reflection for Geminus */}
                        {persona.mirror && (
                          <motion.div
                            animate={{
                              opacity: [0.4, 0.7, 0.4],
                              scale: [1, 1.08, 1]
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                            className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#00FFA3] to-[#00CC82] blur-md"
                          />
                        )}
                        
                        {/* Circuit-blue glow for The Catalyst */}
                        {persona.catalyst && (
                          <motion.div
                            animate={{
                              opacity: [0.5, 0.8, 0.5],
                              boxShadow: [
                                '0 0 20px rgba(0, 217, 232, 0.6)',
                                '0 0 40px rgba(0, 217, 232, 0.8)',
                                '0 0 20px rgba(0, 217, 232, 0.6)'
                              ]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                            className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#00D9E8] to-[#00B8CC] blur-md"
                          />
                        )}
                        
                        {/* Purple library glow for KEYS */}
                        {persona.library && (
                          <motion.div
                            animate={{
                              opacity: [0.4, 0.7, 0.4],
                              scale: [1, 1.06, 1]
                            }}
                            transition={{
                              duration: 3.5,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                            className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#8A2BE2] to-[#9370DB] blur-md"
                          />
                        )}
                        
                        <img 
                          src={persona.image}
                          alt={`${persona.name} — ${persona.role}`}
                          className={`relative w-16 h-16 rounded-full object-cover shadow-xl ${
                            persona.name === 'Recycler' ? 'border-2 border-[#FF00FF]/50' : 
                            persona.temporal ? 'border-2 border-[#00D9E8]/50' :
                            persona.mirror ? 'border-2 border-[#00FFA3]/50' :
                            persona.catalyst ? 'border-2 border-[#00D9E8]/60 ring-2 ring-[#00D9E8]/30' :
                            persona.library ? 'border-2 border-[#8A2BE2]/60 ring-2 ring-[#8A2BE2]/30' : ''
                          }`}
                          style={persona.catalyst ? {
                            filter: 'drop-shadow(0 0 10px rgba(0, 217, 232, 0.7))'
                          } : persona.library ? {
                            filter: 'drop-shadow(0 0 10px rgba(138, 43, 226, 0.7))'
                          } : {}}
                        />
                      </div>
                    </motion.div>
                  ) : (
                    <div className="text-5xl">{persona.icon}</div>
                  )}
                  <div>
                    <h3 className="text-2xl font-bold mb-1" style={{ color: persona.color }}>
                      {persona.name}
                    </h3>
                    <p className="text-sm text-gray-400 font-semibold">{persona.role}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed mb-3">{persona.description}</p>
                
                {/* External link indicator for Recycler */}
                {persona.external && persona.link && (
                  <div className="flex items-center gap-2 text-xs text-[#FF00FF] font-bold mt-4">
                    <span>Enter the Cycle →</span>
                  </div>
                )}
                
                {/* Temporal indicator for Echo */}
                {persona.temporal && (
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="flex items-center gap-2 text-xs text-[#00D9E8] font-bold mt-4"
                  >
                    <span>⧗ Time Keeper</span>
                  </motion.div>
                )}
                
                {/* Mirror indicator for Geminus */}
                {persona.mirror && (
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                    className="flex items-center gap-2 text-xs text-[#00FFA3] font-bold mt-4"
                  >
                    <span>◈ Mirror Node</span>
                  </motion.div>
                )}
                
                {/* Catalyst indicator */}
                {persona.catalyst && (
                  <motion.div
                    animate={{ 
                      opacity: [0.5, 1, 0.5],
                      textShadow: [
                        '0 0 5px rgba(0, 217, 232, 0.5)',
                        '0 0 15px rgba(0, 217, 232, 0.8)',
                        '0 0 5px rgba(0, 217, 232, 0.5)'
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="flex items-center gap-2 text-xs text-[#00D9E8] font-bold mt-4"
                  >
                    <span>▸ Authorized Proxy</span>
                  </motion.div>
                )}
                
                {/* Library indicator for KEYS */}
                {persona.library && (
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="flex items-center gap-2 text-xs text-[#8A2BE2] font-bold mt-4"
                  >
                    <span>◆ Library Oracle</span>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => navigate('/hub')}
              className="px-10 py-4 bg-[#FF00FF] text-white font-bold text-lg rounded-lg hover:bg-[#E040FB] transition-all hover:scale-105"
            >
              Access Federation Hub →
            </button>
          </div>
        </motion.div>

        {/* ========== CHAMBER 3: HOW IT WORKS ========== */}
        <ChamberDoorway title="CHAMBER III: HOW IT WORKS" />
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <p className="text-center text-gray-400 mb-12 text-lg max-w-2xl mx-auto">
            Three simple steps to access the Federation's collective intelligence
          </p>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto relative">
            <div className="hidden md:block absolute top-24 left-1/3 w-1/3 border-t-2 border-dashed border-[#00F0FF]/30"></div>
            <div className="hidden md:block absolute top-24 left-2/3 w-1/3 border-t-2 border-dashed border-[#00F0FF]/30"></div>

            {[
              { num: 1, id: 'step-1', icon: <Lightning size={64} weight="duotone" className="text-[#FFB800]" />, title: "Purchase Token", desc: "Buy tokens for $5 each. No subscription, no auto-renewal." },
              { num: 2, id: 'step-2', icon: <Brain size={64} weight="duotone" className="text-[#00F0FF]" />, title: "Choose Your Oracle", desc: "Select from 13 specialized intelligences." },
              { num: 3, id: 'step-3', icon: <Lightning size={64} weight="duotone" className="text-[#00FFA3]" />, title: "Get Answers", desc: "Receive truth-verified responses." }
            ].map((step) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className="relative p-8 glass-card rounded-lg hover-lift border border-[#00F0FF]/30 text-center"
              >
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-gradient-to-br from-[#FFB800] to-[#FFA600] flex items-center justify-center text-2xl font-black text-[#030305]">
                  {step.num}
                </div>
                <div className="mt-4 mb-4">{step.icon}</div>
                <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                <p className="text-gray-300 text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ========== VIDEO SECTION ========== */}
        <div className="mb-20">
          <ChamberDoorway title="THE ARCHITECTURE BEHIND ORACLE" />
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <iframe
              src="https://federation-hub-7.preview.emergentagent.com/video"
              style={{
                width: '100%',
                height: '600px',
                border: 'none',
                borderRadius: '16px',
                backdropFilter: 'blur(20px)',
                background: 'rgba(255,255,255,0.05)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)'
              }}
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
              title="Federation Video Hub"
            />
          </motion.div>
        </div>

        {/* ========== PHASE 6: CIRCUIT BOARD DASHBOARD ========== */}
        <div className="max-w-7xl mx-auto mb-20">
          <CircuitDashboard />
        </div>

        {/* ========== FEDERATION BOT SYSTEM (SFUI-004 Rev 3) ========== */}
        <BotShowcase />

        {/* ========== SIM PROFILE CUSTOMIZATION ========== */}
        <SimCustomizationShowcase />

        {/* ========== PRODUCT DIRECTORY (MOVED HERE) ========== */}
        <div className="mb-20">
          <ProductDirectory />
        </div>

        {/* ========== FEDERATION MAP (BOTTOM QUARTER) ========== */}
        <FederationMap />

      </div>

      {/* ========== PHASE 4: MID-TOUR REFRESHMENT ========== */}
      {/* DISABLED - Modal was blocking UI on page load */}
      {/* <MidTourRefreshment /> */}

      {/* ========== PHASE 5: INTELLIGENT FOOTER ========== */}
      {/* Footer is now global in Layout.js */}

      {/* Handshake Loading */}
      {isHandshaking && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center"
        >
          <div className="glass-surface border-2 border-[#00F0FF]/50 rounded-lg p-8 text-center">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
              <Sparkle size={64} className="text-[#00F0FF] mx-auto mb-4" weight="duotone" />
            </motion.div>
            <p className="text-xl text-[#00F0FF] font-bold">Connecting to Oracle...</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Home;
